// src/Sections/HeroSection/HeroSection.tsx
import React, { useMemo } from 'react';
import { useTheme } from '@/theme/ThemeProvider';
import { useI18n } from '@/i18n/LanguageProvider';

import {
  SECTION_ONE_IMAGE_RESIZE_WEBP_BASE,
  SECTION_ONE_IMAGE_RESIZE_WEBP_SRC,
  type SectionOneOverrides as HeroSectionOverrides,
} from '@/constants/core';

import Logo from '@/components/Logo/Logo';
import Vid from '@/components/Vid/Vid';
import BaseTitle from '@/components/StyledComponents/Title';
import Description from '@/components/StyledComponents/Description';

import {
  HeroSectionContainer,
  ShortViewportGuard,
  TopContainer,
  CenterContainer,
  BottomContainer,
  LogoWrapper,
  DescriptionWrapper,
  DescriptionPad,
  VideoBox,
  Circle,
} from './styles';

type Props = { overrides?: HeroSectionOverrides };

const HeroSection: React.FC<Props> = ({ overrides }) => {
  const { theme } = useTheme();
  const { i18n } = useI18n();

  const title = overrides?.title;

  const lines = useMemo(
    () => overrides?.descriptionLines,
    [overrides?.descriptionLines],
  );

  const circleBase = SECTION_ONE_IMAGE_RESIZE_WEBP_BASE;
  const circleSrc = SECTION_ONE_IMAGE_RESIZE_WEBP_SRC;

  return (
    <ShortViewportGuard>
      <HeroSectionContainer>
        <TopContainer>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>

          {/* H1 אמיתי, נקי, ברור */}
          <BaseTitle as="h1" itemProp="headline" mobileScale={0.92}>
            {title}
          </BaseTitle>

          <DescriptionWrapper>
            <DescriptionPad>
              <Description aria-hidden="true" fontFamily="rubikBoldItalic">
                {lines?.map((line, i) => (
                  <React.Fragment key={`section1_ui_line_${i}`}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </Description>
            </DescriptionPad>
          </DescriptionWrapper>
        </TopContainer>

        <CenterContainer>
          <VideoBox>
            <Vid title={title} />
          </VideoBox>
        </CenterContainer>

        <BottomContainer>
          <Circle
            idPrefix="section1_circle"
            src={circleSrc}
            alt={`${i18n.BRAND_TITLE_SHORT} | DJ לאירועים`}
            intrinsicSize={{ width: 512, height: 512 }}
            text=" ★ ★ ★ ★ ★ ★ ★ DJ ELIOR MOR "
            spinSeconds={18}
            srcSet={[
              `${circleBase}-256.webp 256w`,
              `${circleBase}-384.webp 384w`,
              `${circleBase}-512.webp 512w`,
            ].join(', ')}
            sizes="(max-width: 768px) 160px, 180px"
            textGradient={{
              from: theme.titleGradientColors?.[0] ?? theme.gradientColors[0],
              via: theme.titleGradientColors?.[1] ?? theme.titleColor,
              to: theme.titleGradientColors?.[2] ?? theme.gradientColors[1],
              angle: 135,
            }}
          />
        </BottomContainer>
      </HeroSectionContainer>
    </ShortViewportGuard>
  );
};

export default HeroSection;
